import React, { Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import Layout from "@components/Layout"
import LoadingPage from "@components/loadingPage/LoadingPage"
import { GlobalContext } from "@tools/globalContext";
import useLazyLoading from "@hooks/useLazyLoading";
import { IComponentList } from "@tools/CustomInterfaces";
import { EPageType } from "@extension/config/pagesConfig";
import { capitalizeFirstLetter } from "@tools/capitalizeFirstLetter";
import ErrorBoundary from "@components/error/ErrorBoundary";
import ConnectionErrorModal from "@components/ConnectionErrorModal";
import NewVersionModal from "@components/NewVersionModal";
import useUserRights from "@hooks/useUserRights"
import * as webSocket from "@tools/webSocket/webSocket";
import useNotificationController from "@components/notifications/useNotificationController";
import useSocketEventManipulator from '@extension/useSocketEventManipulator';
import { useEventEmitter } from '@hooks/useEventEmitter';
import GlobalPlugins from '@extension/globalPlugins';
const defaultPagesPath = "page";

function App() {
    const { user, pagesConfig, pagesDecorator, addSocketEvent, hydraDirectory } = useContext(GlobalContext);
    const { navigatorVisibility, firstPageUrl, findPath } = useUserRights();
    const { set } = useLazyLoading({ defaultComponent: `${hydraDirectory}components/missingLazyComponent/PageUnderConstriction` });
    const { emit } = useEventEmitter();
    useSocketEventManipulator();
    const { contextHolder } = useNotificationController();

    useEffect(() => {
        let socket = webSocket.on("message", async (data: any) => {
            const eventData = await emit("beforeSocketEvent", data)
            // let newData = eventData?.data || data;
            // addSocketEvent({value: {[data.data._id] : newData}});
        });

        return (() => {
            socket.off()
        })
    }, [addSocketEvent, emit]);

    const pages = useMemo(() => {
        const result: IComponentList[] = [];

        const buildPagesMap = (arr: any[], path: string[]) => {
            arr.forEach(page => {
                const pageInfo: any = pagesConfig.pages.find(it => it.id === page.id)
                if (!pageInfo) {
                    console.error(`Missing page "${page.id}". This page presents in decorator but missing in the page config file!`)
                    return
                }
                const urlPath = findPath(page.id)

                if (navigatorVisibility(pageInfo.id)) {
                    result.push({ name: urlPath, path: pageInfo.componentPath || defaultPagesPath, component: capitalizeFirstLetter(pageInfo.component || pageInfo.id) })
                    if (pageInfo.type === EPageType.tabs) {
                        result.push({ name: urlPath + "/:tab", path: pageInfo.componentPath || defaultPagesPath, component: capitalizeFirstLetter(pageInfo.component || pageInfo.id) })
                    }
                }
                else {
                    result.push({ name: urlPath, path: `${hydraDirectory}components/missingLazyComponent`, component: "PageAccessDenied" })
                }
                if (page.children)
                    buildPagesMap(page.children, [...path])
            })
        }

        buildPagesMap(pagesDecorator, []);

        return set(result);
    }, [pagesConfig, pagesDecorator, set, findPath, navigatorVisibility])

    const NotFoundPage = useMemo((): React.FC => {
        return pages.get(`${pagesConfig.rootUrl ? `/${pagesConfig.rootUrl}` : ""}/notFound`)
    }, [pages, pagesConfig])

    const renderRoutePages = useCallback((config: any[], path?: string[]): any[] => {
        let result: any[] = [];

        const addPage = (urlPath: string) => {
            const Page = pages.get(urlPath);
            if (Page) {
                result.push(<Route key={urlPath} path={urlPath} element={
                    <ErrorBoundary>
                        <Suspense fallback={<LoadingPage />}>
                            <Page />
                        </Suspense>
                    </ErrorBoundary>}
                />)
            }
        }
        config.forEach(item => {
            const urlPath = findPath(item.id)
            const pageConfig = pagesConfig.pages.find(it => it.id === item.id)
            if (item?.redirectTo) {
                const link = item.redirectTo[user.type] || item.redirectTo.all
                result.push(<Route key={urlPath} path={urlPath} element={<Navigate to={findPath(link, pageConfig?.id)} replace />} />)
            }
            if (pageConfig?.type === "tabs") {
                result.push(<Route key={urlPath} path={urlPath} element={<Navigate to={`./${pageConfig.defaultTab || "info"}`} replace />} />)
                addPage(urlPath + "/:tab")
            }
            else {
                addPage(urlPath)
            }

            if (item.children && item.children.length)
                result = [...result, ...renderRoutePages(item.children, [...path || []])]
        })
        return result;
    }, [pages, findPath, pagesConfig])

    const allPages = useMemo(() => {
        return renderRoutePages(pagesDecorator)
    }, [pagesDecorator])

    return <BrowserRouter>
        <ConnectionErrorModal />
        <NewVersionModal />
        <GlobalPlugins />
        <Layout>
            {contextHolder}
                <Routes>
                    {!pagesConfig.rootUrl && <Route path="/" element={<Navigate to={firstPageUrl} replace />} />}
                    <Route path={`${pagesConfig.rootUrl ? `/${pagesConfig.rootUrl}` : ""}/`} element={<Navigate to={firstPageUrl} replace />} />
                    {allPages}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
        </Layout>
    </BrowserRouter>
}

export default App;
